// Everything that can take focus from the Tab key, before the checks below
// take out what is disabled, hidden or has `tabindex="-1"`.
const CANDIDATES = [
  'a[href]',
  'area[href]',
  'audio[controls]',
  'button',
  'iframe',
  'input:not([type="hidden"])',
  'select',
  'summary',
  'textarea',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(', ');

// The elements that are a control: they do something when a person presses
// them or types in them. An element that takes focus only to show a tooltip,
// such as the "Scheduled" mark of an episode, is not one.
export const INTERACTIVE = [
  'a[href]',
  'button',
  'input',
  'select',
  'summary',
  'textarea',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
].join(', ');

// Controls a person types into or picks a value with, as opposed to buttons
// and links. A dialog puts focus on the first of these when it opens.
const FIELDS = [
  'input:not([type="button"]):not([type="image"]):not([type="reset"]):not([type="submit"])',
  'select',
  'textarea',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="checkbox"]',
  '[role="combobox"]',
  '[role="radio"]',
  '[role="searchbox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="switch"]',
  '[role="textbox"]',
].join(', ');

// Browsers report -1 from `tabIndex` for some elements that are in the tab
// order all the same, when they have no `tabindex` attribute of their own.
const tabIndexOf = (element: HTMLElement): number => {
  if (
    !element.hasAttribute('tabindex') &&
    (element.isContentEditable ||
      element.getAttribute('contenteditable') !== null ||
      /^(AUDIO|VIDEO|SUMMARY)$/.test(element.tagName))
  ) {
    return 0;
  }
  return element.tabIndex;
};

const isHidden = (element: HTMLElement, root: Element): boolean => {
  if (element.closest('[inert]')) {
    return true;
  }
  // Browsers answer this from layout, which also covers a closed <details>.
  if (typeof element.checkVisibility === 'function') {
    return !element.checkVisibility({
      checkVisibilityCSS: true,
      visibilityProperty: true,
    });
  }
  // jsdom has no layout, so fall back to the styles that hide an element.
  if (getComputedStyle(element).visibility === 'hidden') {
    return true;
  }
  for (
    let node: Element | null = element;
    node && node !== root.parentElement;
    node = node.parentElement
  ) {
    if (
      (node as HTMLElement).hidden ||
      getComputedStyle(node).display === 'none'
    ) {
      return true;
    }
  }
  return false;
};

// Only one radio button of a group is in the tab order: the checked one. With
// none checked, Tab lands on the first and Shift+Tab on the last, so all of
// them stay in the list and the ends of the list come out right both ways.
const isSkippedRadio = (element: HTMLElement, root: Element): boolean => {
  if (!(element instanceof HTMLInputElement) || element.type !== 'radio') {
    return false;
  }
  if (!element.name || element.checked) {
    return false;
  }
  const group = element.form ?? root;
  return Array.from(
    group.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
  ).some(radio => radio.name === element.name && radio.checked);
};

/**
 * The elements in `root` that the Tab key can reach, in document order. It
 * leaves out what is disabled, hidden, inert or has `tabindex="-1"`.
 */
export const getTabbable = (root: Element): Array<HTMLElement> =>
  Array.from(root.querySelectorAll<HTMLElement>(CANDIDATES)).filter(
    element =>
      tabIndexOf(element) >= 0 &&
      !element.matches(':disabled') &&
      !isHidden(element, root) &&
      !isSkippedRadio(element, root),
  );

/** The first field in `root` that the Tab key can reach, if there is one. */
export const getFirstField = (root: Element): HTMLElement | null =>
  getTabbable(root).find(element => element.matches(FIELDS)) ?? null;
